import "swiper/swiper.css";

import { hooks, state } from "@springtree/eva-sdk-react-recoil";
import { Core } from "@springtree/eva-services-core";
import { useQuery } from "@tanstack/react-query";
import classNames from "classnames";
import { useTranslations } from "next-intl";
import { useEffect, useMemo, useState } from "react";
import SwiperCore, { Controller } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";

import { useWindowSize } from "~/contexts/breakpoint";
import { useTextDir } from "~/contexts/text-dir";
import { useCart, useCartWithHooks } from "~/hooks/use-cart";
import CrossCountry from "~/types/cross-country";
import Constants from "~/utils/constants";
import productUtils from "~/utils/product-utils";

import Icon from "../common/icon";
import styles from "./cart-pick-product-block.module.scss";
import CartPickProductOption from "./cart-pick-product-option";
import CartPickProductParentOption from "./cart-pick-product-parent-option";

type ParentProduct = CrossCountry.ChildrenProduct & { children: CrossCountry.ChildrenProduct[] };
type StandaloneProduct = CrossCountry.ChildrenProduct;

type Props = {
  line: string;
  option: EVA.Core.OrderPickProductDiscountOptions;
  currentSelection: number | undefined;
};

const WIDTH_BULLETS_CONTAINER = 150;
const BULLETS_SLIDES_PER_VIEW_DEFAULT = 3;

function isParentProduct(product: unknown): product is ParentProduct {
  return !!product && typeof product === "object" && "children" in product && Array.isArray(product.children);
}

export default function CartPickProductBlock(props: Props) {
  const t = useTranslations();
  const dir = useTextDir();
  const cart = useCart();
  const { setPickProduct } = useCartWithHooks();
  const { width } = useWindowSize();

  const GWP_PRODUCTS_TO_SHOW = width && width > 1280 ? 4 : 3;
  const evaEndpointUrl = hooks.useGetState(state.core.evaEndpointUrlState);
  const searchProducts = hooks.useCallService({ service: Core.SearchProducts });

  const [currentSlide, setCurrentSlide] = useState(0);
  const [swiperProductsInstance, setSwiperProductsInstance] = useState<SwiperCore>();
  const [swiperBulletsInstance, setSwiperBulletsInstance] = useState<SwiperCore>();

  const tier = props.option.Tiers.find((tier) => tier.IsCurrentTier);
  const tierProducts = tier?.Products;

  const promoLine = cart?.ShoppingCart.Lines.find((el) => `${el.ID}` == props.line);

  const parentsQuery = useQuery({
    queryKey: ["cart", cart?.ShoppingCart.ID, "pickProductBlock", props.line, "parents"],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const parents = new Set();

      for (const product of tierProducts!) {
        if (product.logical_level === "color") {
          const parentId = productUtils.getProductParentId(product);

          if (parentId) {
            parents.add(parentId);
          }
        }
      }

      const arrayParents = Array.from(parents);
      const searched =
        arrayParents.length > 0
          ? await searchProducts({
              IncludedFields: ["*"],
              Filters: { product_id: { Values: Array.from(parents) } },
              PageSize: 100,
            })
          : undefined;

      return (
        searched?.Products?.map((parent) => {
          const childrenProducts = tierProducts!.filter(
            (p) => productUtils.getProductParentId(p) === parent.product_id
          );

          return { ...parent, children: childrenProducts };
        }) ?? []
      );
    },
    enabled: !!evaEndpointUrl && tierProducts && tierProducts.length > 0,
  });

  const allProducts = useMemo(() => {
    if (!tierProducts || !parentsQuery.data) {
      // No products to display
      return null;
    }

    const output: (ParentProduct | StandaloneProduct)[] = [];

    for (const product of tierProducts) {
      if (product.logical_level === "color") {
        const parentId = productUtils.getProductParentId(product);
        const parentProduct = parentsQuery.data.find((p) => p.product_id === parentId);

        if (parentId! && parentProduct && !output.find((p) => p.product_id === Number(parentId))) {
          output.push(parentProduct);
        }
      } else {
        output.push(product);
      }
    }

    return output;
  }, [tierProducts, parentsQuery.data]);

  async function selectPickGift(productId: number) {
    return await setPickProduct({ payload: { OrderLineID: +props.line, Selection: productId } });
  }

  useEffect(() => {
    if (tier && !props.option.CurrentSelection) {
      // Select the first gift by default
      setPickProduct({ payload: { OrderLineID: +props.line, Selection: props.option.Options[0] } });
    }
  }, [tier, props.option.CurrentSelection, props.option.Options, props.line, setPickProduct]);

  if (!tier || !allProducts || allProducts.length === 0) {
    return null;
  }

  const allProductsInGroups = [];
  for (let i = 0; i < allProducts.length; i += GWP_PRODUCTS_TO_SHOW) {
    const group = allProducts.slice(i, i + GWP_PRODUCTS_TO_SHOW);
    allProductsInGroups.push(group);
  }

  return (
    <div className={styles.container}>
      <div className={styles.textContainer}>
        <p className={styles.title}>{promoLine?.Description || tier.Description}</p>
        <p className={styles.subtitle}>{t("generic.giftedProductsDesc")}</p>
        {/* TODO understand when to show those labels reserved to loyalty promotion */}
        {/* <p className={styles.headline}>{t("generic.cart.kisses.headline")}</p>
      <p className={styles.title}>{t("generic.cart.kisses.title")}</p>
      <p className={styles.subtitle}>{t("generic.cart.kisses.subtitle")}</p> */}
      </div>
      <Swiper
        modules={[Controller]}
        onSwiper={(swiper) => {
          setSwiperProductsInstance(swiper);
          const findIndexProductInGroup = Math.floor(
            allProducts.findIndex((product) => {
              if (product.children) {
                const child = product.children.find((child) => child.product_id === props.currentSelection);
                return !!child;
              } else {
                return product.product_id === props.currentSelection;
              }
            }) / GWP_PRODUCTS_TO_SHOW
          );
          if (findIndexProductInGroup !== -1) swiper.activeIndex = findIndexProductInGroup;
        }}
        controller={{ control: swiperBulletsInstance }}
        grabCursor
        slidesPerView={1}
        spaceBetween={12}
        speed={Constants.SWIPER_TRANSITION_DURATION}
        onTransitionEnd={(currentSwiper) => setCurrentSlide(currentSwiper.activeIndex)}
      >
        {allProductsInGroups.map((productsGroup, i) => (
          <SwiperSlide
            key={"products group " + i}
            className={classNames(styles.productsContainer, i > 0 ? styles.setProductRows : undefined)}
          >
            {productsGroup.map((product) => {
              if (isParentProduct(product)) {
                return (
                  <CartPickProductParentOption
                    key={product.product_id + "_" + props.currentSelection}
                    product={product}
                    currentSelection={props.currentSelection}
                    onSelect={selectPickGift}
                  />
                );
              } else {
                return (
                  <CartPickProductOption
                    key={product.product_id}
                    product={product}
                    currentSelection={props.currentSelection}
                    onSelect={selectPickGift}
                  />
                );
              }
            })}
          </SwiperSlide>
        ))}
      </Swiper>
      {allProductsInGroups.length > 1 && (
        <div className={styles.bulletsContainer}>
          <Swiper
            modules={[Controller]}
            onSwiper={setSwiperBulletsInstance}
            grabCursor
            slidesPerView={allProductsInGroups.length === 2 ? 2 : BULLETS_SLIDES_PER_VIEW_DEFAULT}
            centeredSlides
            centeredSlidesBounds
            className={styles.wrapperBullets}
            style={{
              width:
                allProductsInGroups.length === 2
                  ? WIDTH_BULLETS_CONTAINER - WIDTH_BULLETS_CONTAINER / BULLETS_SLIDES_PER_VIEW_DEFAULT
                  : WIDTH_BULLETS_CONTAINER,
            }}
            onSetTranslate={(currentSwiper, translate) => {
              //edge case: a CSS transform rule is applied to the second element of the swiper for a reason that is not clear to me. It was forced to 0.
              //N.B. pages RTL: onSetTranslate is not detected on the first render of the page if the inspector is open.
              if (allProductsInGroups.length > BULLETS_SLIDES_PER_VIEW_DEFAULT) {
                const maxWidthBullet = WIDTH_BULLETS_CONTAINER / BULLETS_SLIDES_PER_VIEW_DEFAULT;
                if (dir === "rtl" ? translate < maxWidthBullet : translate > -maxWidthBullet) {
                  currentSwiper.wrapperEl.style.transform = "translate3d(0px, 0px, 0px)";
                }
              }
            }}
            speed={Constants.SWIPER_TRANSITION_DURATION}
          >
            {allProductsInGroups.map((_, i) => (
              <SwiperSlide key={"bullet " + i} className={styles.bulletSlide}>
                <button
                  onClick={() => {
                    swiperProductsInstance?.slideTo(i);
                  }}
                  aria-label={
                    t.rich("a11y.goToSlide", {
                      number: i + 1,
                    }) as string
                  }
                  className={classNames(styles.bullet, i === currentSlide ? styles.bulletActive : undefined)}
                >
                  <span className={styles.bulletNumber}>{i + 1}</span>
                </button>
              </SwiperSlide>
            ))}
          </Swiper>
          <div className={classNames(styles.arrowsContainer)}>
            <button
              className={styles.arrowL}
              disabled={swiperProductsInstance?.isBeginning}
              onClick={() => swiperProductsInstance?.slidePrev()}
              aria-label={t("a11y.previousSlide")}
            >
              <Icon name="chevron-left" width={14} height={14} />
            </button>
            <button
              className={styles.arrowR}
              disabled={swiperProductsInstance?.isEnd}
              onClick={() => swiperProductsInstance?.slideNext()}
              aria-label={t("a11y.nextSlide")}
            >
              <Icon name="chevron-right" width={14} height={14} />
            </button>
          </div>
          {allProductsInGroups.length > BULLETS_SLIDES_PER_VIEW_DEFAULT && (
            <div className={classNames(styles.doublearrowsContainer)}>
              <button
                className={styles.arrowL}
                disabled={swiperProductsInstance?.isBeginning}
                onClick={() => swiperProductsInstance?.slideTo(0)}
                aria-label={t("a11y.goToFirstSlide")}
              >
                <Icon
                  name="double-chevron-down"
                  width={14}
                  height={14}
                  className={dir === "rtl" ? styles.rotateDoubleArrowIconRTL : styles.rotateDoubleArrowIcon}
                />
              </button>
              <button
                className={styles.arrowR}
                disabled={swiperProductsInstance?.isEnd}
                onClick={() => swiperProductsInstance?.slideTo(allProductsInGroups.length - 1)}
                aria-label={t("a11y.goToLastSlide")}
              >
                <Icon
                  name="double-chevron-up"
                  width={14}
                  height={14}
                  className={dir === "rtl" ? styles.rotateDoubleArrowIconRTL : styles.rotateDoubleArrowIcon}
                />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
